import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from './client.js';
import type { Tenant } from './types.js';
import type { UserLookup } from './users.js';

export function useTenant(tenantId: string) {
  return useQuery<Tenant>({
    queryKey: ['tenant', tenantId],
    queryFn: () => apiFetch<Tenant>(`/tenants/${encodeURIComponent(tenantId)}`),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
}

export interface UpdateTenantPayload {
  defaultComptableUid: string | null;
}

export interface DefaultValidator {
  uid: string;
  displayName: string | null;
  email: string | null;
}

const HOUR = 60 * 60 * 1000;

/**
 * Resolve the tenant's `defaultValidatorUids` (REQ-BE-018) into a list of
 * user records suitable for a suggestion dropdown. Empty array when the
 * tenant has no defaults, the tenant query is still loading, or every uid
 * 404s. Uids that 404 are silently dropped (rather than surfaced as a
 * broken row).
 */
export function useTenantDefaultValidators(tenantId: string): DefaultValidator[] {
  const tenantQ = useTenant(tenantId);
  const uids = (tenantQ.data?.defaultValidatorUids ?? []).filter((u): u is string => Boolean(u));
  const userQueries = useQueries({
    queries: uids.map((uid) => ({
      queryKey: ['user-by-uid', uid] as const,
      queryFn: async (): Promise<UserLookup | null> => {
        try {
          return await apiFetch<UserLookup>(`/users/by-uid?uid=${encodeURIComponent(uid)}`);
        } catch (e) {
          if (e instanceof ApiError && e.status === 404) return null;
          throw e;
        }
      },
      staleTime: HOUR,
      gcTime: HOUR * 24,
    })),
  });
  const out: DefaultValidator[] = [];
  uids.forEach((uid, i) => {
    const u = userQueries[i]?.data;
    if (!u) return;
    out.push({ uid, displayName: u.displayName ?? null, email: u.email ?? null });
  });
  return out;
}

export function useUpdateTenant(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<Tenant, Error, UpdateTenantPayload>({
    mutationFn: (payload) =>
      apiFetch<Tenant>(`/tenants/${encodeURIComponent(tenantId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    onSuccess: (next) => {
      qc.setQueryData(['tenant', tenantId], next);
      void qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}
